import { useState } from 'react';
import { ChevronDownIcon, ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// Right-side detail drawer used by billing pages (charges, invoices, one-time discounts).

interface DrawerProps {
    title: React.ReactNode;
    badge?: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Rendered left of the title block, outside the truncating h2 (e.g. an avatar). */
    leading?: React.ReactNode;
    onClose: () => void;
    // Renders a back chevron before the title, for drawer-to-drawer navigation
    // (e.g. invoice -> charge and back).
    onBack?: () => void;
    // Blocks Escape and the overlay click while a save or delete is in flight,
    // so a mutation can't be abandoned mid-request.
    closeDisabled?: boolean;
    /** Pinned bar below the scroll region, e.g. a save action. Omit for no footer. */
    footer?: React.ReactNode;
    children: React.ReactNode;
}

export function Drawer({
    title,
    badge,
    subtitle,
    leading,
    onClose,
    onBack,
    closeDisabled = false,
    footer,
    children
}: DrawerProps) {
    useEscapeKey(onClose, !closeDisabled);

    return (
        <>
            {/* Click-out stays - a drawer is peripheral, unlike a modal where a stray
                click discards a form. Off while closeDisabled (saving, overlay above). */}
            <div
                className="fixed inset-0 bg-black bg-opacity-20 z-40"
                onClick={closeDisabled ? undefined : onClose}
            />
            <div className="fixed right-0 top-0 h-full w-full max-w-[560px] bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 shrink-0 bg-white">
                    <div
                        className={`flex ${leading ? 'items-center gap-3' : 'items-start gap-2'} min-w-0`}
                    >
                        {leading}
                        {onBack && (
                            <button
                                type="button"
                                onClick={onBack}
                                aria-label="Back"
                                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 shrink-0 transition-colors -ml-2"
                            >
                                <ChevronLeftIcon className="h-5 w-5" />
                            </button>
                        )}
                        <div className="min-w-0">
                            <div className="flex items-center gap-2.5">
                                <h2 className="text-xl font-bold text-gray-800 truncate">
                                    {title}
                                </h2>
                                {badge}
                            </div>
                            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={closeDisabled}
                        aria-label="Close"
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-6">
                    {children}
                </div>
                {footer && (
                    <div className="px-6 py-4 border-t border-gray-200 shrink-0 bg-white">
                        {footer}
                    </div>
                )}
            </div>
        </>
    );
}

export function DrawerSection({
    title,
    action,
    collapsible = false,
    defaultOpen = true,
    children
}: {
    title: string;
    action?: React.ReactNode;
    // Collapsible sections render their children only while open, so folded
    // content costs nothing (queries inside don't even fire).
    collapsible?: boolean;
    defaultOpen?: boolean;
    children: React.ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);
    const isOpen = !collapsible || open;
    return (
        <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                {collapsible ? (
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="flex items-center gap-1.5 group"
                    >
                        <h3 className="text-[12px] font-bold tracking-[0.005em] text-neutral-500 uppercase group-hover:text-neutral-700 transition-colors">
                            {title}
                        </h3>
                        <ChevronDownIcon
                            className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                        />
                    </button>
                ) : (
                    <h3 className="text-[12px] font-bold tracking-[0.005em] text-neutral-500 uppercase">
                        {title}
                    </h3>
                )}
                {action}
            </div>
            {isOpen && children}
        </section>
    );
}

export function KVRow({ k, children }: { k: string; children: React.ReactNode }) {
    return (
        <div className="flex items-start justify-between gap-4 py-1">
            <span className="text-sm text-gray-500 shrink-0">{k}</span>
            <span className="text-sm text-gray-800 text-right">{children}</span>
        </div>
    );
}
